import React from 'react';
import { useIntl, defineMessage } from 'react-intl';
import { Table } from 'reactstrap';
import { faPlayPause } from '@fortawesome/pro-light-svg-icons/faPlayPause';
import { TypedMemo } from '../../../common/types';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLLink } from '../SMLLink/SMLLink';
import { PercentageChangeElement } from '../PositionChangeElement';
import { LoadingView } from '../../../common/views/LoadingView';
import { SMLImage } from '../SMLImage';
import { SMLEmptyState } from '../SMLEmptyState/SMLEmptyState';
import { useTrackStats } from '../../hooks/useTrackStats';
import { MiniScoreChart } from '../MiniScoreChart/MiniScoreChart';
import styles from './TracksTable.module.scss';

// Basic track data returned by listFollowed endpoint
export interface TrackBasicData {
  id: string;
  name: string;
  artistName: string;
  imageUrl?: string | null;
}

// Full track data with stats (for backwards compatibility)
export interface TrackTableData extends TrackBasicData {
  addedToPlaylists: number;
  addedToPlaylistsChange: number | null;
  removedFromPlaylists: number;
  removedFromPlaylistsChange: number | null;
  appearedInPlaylists: number;
  appearedInPlaylistsChange: number | null;
}

export interface TracksTableProps {
  tracks: TrackBasicData[];
  isLoading?: boolean;
  selectedDateValue?: string;
  selectedDate?: Date;
}

const messages = {
  trackName: defineMessage({
    id: 'app.sml.tracks.table.trackName',
    defaultMessage: 'Track Name',
  }),
  artist: defineMessage({
    id: 'app.sml.tracks.table.artist',
    defaultMessage: 'Artist',
  }),
  playlistGainScore: defineMessage({
    id: 'app.sml.tracks.table.playlistGainScore',
    defaultMessage: 'Playlist Gain Score',
  }),
  playlistLossScore: defineMessage({
    id: 'app.sml.tracks.table.playlistLossScore',
    defaultMessage: 'Playlist Loss Score',
  }),
  playlistVisibilityScore: defineMessage({
    id: 'app.sml.tracks.table.playlistVisibilityScore',
    defaultMessage: 'Playlist Visibility Score',
  }),
  trend: defineMessage({
    id: 'app.sml.tracks.table.trend',
    defaultMessage: 'Trend',
  }),
  actions: defineMessage({
    id: 'app.sml.tracks.table.actions',
    defaultMessage: 'Actions',
  }),
  viewDetails: defineMessage({
    id: 'app.sml.tracks.table.viewDetails',
    defaultMessage: 'View Details',
  }),
};

export const TRACKS_EXPORT_FILENAME = 'tracks';

const formatNumber = (value: number): string => {
  return value.toLocaleString('en-US');
};

const renderPlaceholderImage = (): React.ReactNode => (
  <div className={styles.placeholderImage}>
    <div className={styles.placeholderCircle} />
  </div>
);

// Skeleton loader for stats cells
const StatsLoadingSkeleton: React.FC = () => (
  <div className={styles.metricContainer}>
    <span className={styles.metricSkeleton} />
  </div>
);

// Individual track row component that fetches its own stats
interface TrackRowProps {
  track: TrackBasicData;
  selectedDateValue?: string;
  selectedDate?: Date;
}

const TrackRowComponent: React.FC<TrackRowProps> = ({ track, selectedDateValue, selectedDate }) => {
  const intl = useIntl();
  const { additionsStats, appearancesStats, isAdditionsLoading, isAppearancesLoading } = useTrackStats(
    track.id,
    selectedDate
  );

  return (
    <tr>
      <td className={styles.dataCell}>
        <div className={styles.trackInfo}>
          {track.imageUrl ? (
            <SMLImage src={track.imageUrl} alt={track.name} width={24} makeSquareFit className={styles.trackImage} />
          ) : (
            renderPlaceholderImage()
          )}
          <SMLLink
            to={SMLRoutes.trackDetails}
            params={{ trackId: track.id, ...(selectedDateValue !== undefined && { date: selectedDateValue }) }}
            className={styles.trackName}
          >
            {track.name}
          </SMLLink>
        </div>
      </td>

      <td className={styles.dataCell}>
        <span className={styles.artistName}>{track.artistName}</span>
      </td>

      <td className={styles.dataCell}>
        {isAdditionsLoading || !additionsStats ? (
          <StatsLoadingSkeleton />
        ) : (
          <div className={styles.metricContainer}>
            <span className={styles.metricValue}>{formatNumber(additionsStats.addedToPlaylists)}</span>
            <PercentageChangeElement percentageChange={additionsStats.addedToPlaylistsChange} />
          </div>
        )}
      </td>

      <td className={styles.dataCell}>
        {isAdditionsLoading || !additionsStats ? (
          <StatsLoadingSkeleton />
        ) : (
          <div className={styles.metricContainer}>
            <span className={styles.metricValue}>{formatNumber(additionsStats.removedFromPlaylists)}</span>
            <PercentageChangeElement percentageChange={additionsStats.removedFromPlaylistsChange} invertColorLogic />
          </div>
        )}
      </td>

      <td className={styles.dataCell}>
        {isAppearancesLoading || !appearancesStats ? (
          <StatsLoadingSkeleton />
        ) : (
          <div className={styles.metricContainer}>
            <span className={styles.metricValue}>{formatNumber(appearancesStats.appearedInPlaylists)}</span>
            <PercentageChangeElement percentageChange={appearancesStats.appearedInPlaylistsChange} />
          </div>
        )}
      </td>

      <td className={styles.dataCell}>
        <MiniScoreChart entityType="track" entityId={track.id} date={selectedDate} />
      </td>

      <td className={styles.dataCellAction}>
        <SMLLink
          to={SMLRoutes.trackDetails}
          params={{ trackId: track.id, ...(selectedDateValue !== undefined && { date: selectedDateValue }) }}
          className="btn btn-primary btn-sm"
        >
          {intl.formatMessage(messages.viewDetails)}
        </SMLLink>
      </td>
    </tr>
  );
};

const TrackRow = TypedMemo(TrackRowComponent);

const TracksTableComponent: React.FC<TracksTableProps> = ({
  tracks,
  isLoading = false,
  selectedDateValue,
  selectedDate,
}) => {
  const intl = useIntl();

  if (isLoading) {
    return (
      <div className={styles.tableContainer}>
        <LoadingView />
      </div>
    );
  }

  if (tracks.length === 0) {
    return (
      <div className={styles.tableContainer}>
        <SMLEmptyState
          icon={faPlayPause}
          title={intl.formatMessage({
            id: 'app.sml.tracks.table.empty.title',
            defaultMessage: 'No tracks yet',
          })}
          description={intl.formatMessage({
            id: 'app.sml.tracks.table.empty.description',
            defaultMessage: 'Follow some tracks to see their playlist metrics here.',
          })}
        />
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <Table responsive hover className={styles.table}>
        <thead>
          <tr>
            <th className={styles.headerCell}>{intl.formatMessage(messages.trackName)}</th>
            <th className={styles.headerCell}>{intl.formatMessage(messages.artist)}</th>
            <th className={styles.headerCell}>{intl.formatMessage(messages.playlistGainScore)}</th>
            <th className={styles.headerCell}>{intl.formatMessage(messages.playlistLossScore)}</th>
            <th className={styles.headerCell}>{intl.formatMessage(messages.playlistVisibilityScore)}</th>
            <th className={styles.headerCell}>{intl.formatMessage(messages.trend)}</th>
            <th className={styles.headerCellAction}>{intl.formatMessage(messages.actions)}</th>
          </tr>
        </thead>
        <tbody>
          {tracks.map((track) => (
            <TrackRow key={track.id} track={track} selectedDateValue={selectedDateValue} selectedDate={selectedDate} />
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export const TracksTable = TypedMemo(TracksTableComponent);
